import React, { useEffect, useState } from "react";
import { Music2, Film, Video, Users, Camera, Shirt, Smile, ChefHat, Share2, Copy, Check } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const RUBRIQUES = [
  { key: "music",  icon: Music2,  label: "Morceaux",    color: "from-brand-red to-brand-orange" },
  { key: "dance",  icon: Users,   label: "Danses",      color: "from-[#0B8B47] to-brand-yellow" },
  { key: "humor",  icon: Smile,   label: "Sketches",    color: "from-brand-yellow to-brand-orange" },
  { key: "art",    icon: Camera,  label: "Œuvres",      color: "from-pink-500 to-brand-red" },
  { key: "cinema", icon: Film,    label: "Films",       color: "from-purple-500 to-brand-red" },
  { key: "fashion",icon: Shirt,   label: "Défilés",     color: "from-rose-500 to-amber-500" },
  { key: "food",   icon: ChefHat, label: "Recettes",    color: "from-amber-500 to-brand-red" },
];

const CAPTIONS = [
  {
    title: "Instagram — annonce lancement",
    text: `🌱 Voamada est là.\n\nLa plateforme 360° des talents malgaches.\n\n🎵 {music} morceaux\n💃 {dance} danses\n😂 {humor} sketches\n🎨 {art} œuvres\n🎬 {cinema} films\n👗 {fashion} défilés\n\n+{artists} artistes déjà référencés — toi aussi, revendique ton profil.\n\n🔗 voamada.com\n\n#Madagascar #ArtisteGasy #Voamada`,
  },
  {
    title: "Facebook — post communauté",
    text: `Voamada, c'est bien plus qu'une plateforme de musique.\n\nC'est un tremplin pour TOUS les talents malgaches : musiciens, danseurs, humoristes, cinéastes, artistes visuels, créateurs de mode.\n\n➡️ {artists} artistes déjà présents\n➡️ {tracks} contenus disponibles\n\nTu es artiste ? Revendique ton profil gratuitement sur voamada.com.\nTu es marque / festival / média ? Deviens partenaire officiel.\n\n#MadagascarCulture #TalentsGasy`,
  },
  {
    title: "Cold outreach — email sponsor",
    text: `Bonjour {Prénom},\n\nVoamada est la nouvelle plateforme qui rassemble l'écosystème créatif malgache dans un seul espace. Aujourd'hui nous référençons déjà :\n\n• {artists} artistes dans 6 disciplines\n• {tracks} contenus (musique, cinéma, humour, danse, art visuel, mode)\n• Des légendes comme Jaojoby, Rossy, Big MJ, Anatal, et la nouvelle scène rap gasy\n\nNous ouvrons nos premiers partenariats sponsors avec 3 packs (Bronze / Silver / Gold). Seriez-vous disponible 15 minutes pour en discuter ?\n\nBien cordialement,\nL'équipe Voamada — voamada.com`,
  },
];

const interpolate = (tpl, data) =>
  tpl.replace(/\{(\w+)\}/g, (_, k) => data[k] ?? `{${k}}`);

export const PromoStatsWall = () => {
  const [stats, setStats] = useState({ artists: 173, tracks: 230, countries: 13, genres: 33 });
  const [byCategory, setByCategory] = useState({});
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setStats((s) => ({ ...s, ...data }))).catch(() => {});
    // Fetch counts per category
    Promise.all(
      RUBRIQUES.map((r) =>
        api.get("/tracks", { params: { category: r.key, limit: 100 } }).then(({ data }) => [r.key, data.length])
      )
    ).then((entries) => setByCategory(Object.fromEntries(entries))).catch(() => {});
  }, []);

  const doCopy = async (idx, tpl) => {
    const text = interpolate(tpl, {
      ...stats,
      music: byCategory.music ?? "—",
      dance: byCategory.dance ?? "—",
      humor: byCategory.humor ?? "—",
      art: byCategory.art ?? "—",
      cinema: byCategory.cinema ?? "—",
      fashion: byCategory.fashion ?? "—",
      food: byCategory.food ?? "—",
    });
    await navigator.clipboard.writeText(text);
    setCopied(idx);
    toast.success("Texte copié dans le presse-papier");
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="px-4 md:px-12 py-10 max-w-6xl mx-auto pb-24" data-testid="promo-page">
      <p className="text-[10px] tracking-[0.25em] uppercase text-brand-yellow font-bold mb-2">Kit de lancement</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-3">
        Stats wall prêt à partager.
      </h1>
      <p className="text-white/60 max-w-2xl mb-10">
        Screenshot le visuel ci-dessous au format 1080×1080 (Instagram) ou partage les légendes ci-dessous pour annoncer Voamada à ta communauté.
      </p>

      {/* VISUEL — 1080×1080 square (display-scaled) */}
      <div
        id="stats-wall-visual"
        className="relative mx-auto rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-gradient-to-br from-[#1A0F0C] via-[#0F0C0B] to-[#1A0F14]"
        style={{ aspectRatio: "1 / 1", maxWidth: "720px" }}
        data-testid="stats-wall-visual"
      >
        {/* BG pattern */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(ellipse at 20% 20%, rgba(227,27,35,0.25), transparent 55%), radial-gradient(ellipse at 85% 80%, rgba(234,179,8,0.22), transparent 60%), radial-gradient(ellipse at 50% 50%, rgba(249,87,56,0.12), transparent 70%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-20 mix-blend-overlay"
          style={{
            backgroundImage:
              "radial-gradient(1px 1px at 25% 30%, rgba(255,255,255,0.5), transparent), radial-gradient(1.2px 1.2px at 65% 70%, rgba(255,255,255,0.4), transparent), radial-gradient(1px 1px at 85% 25%, rgba(255,255,255,0.35), transparent)",
            backgroundSize: "180px 180px, 220px 220px, 300px 300px",
          }}
        />

        {/* Content */}
        <div className="relative h-full flex flex-col p-8 md:p-10">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <img src="/assets/icon-512.png" alt="" className="w-14 h-14 rounded-xl" />
              <div>
                <p className="font-display font-black text-2xl leading-none">
                  <span className="bg-gradient-to-r from-[#E31B23] via-brand-orange to-[#EAB308] bg-clip-text text-transparent">
                    VOAMADA
                  </span>
                </p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">La voix de Madagascar</p>
              </div>
            </div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-brand-yellow font-bold">Live stats</p>
          </div>

          {/* Big number */}
          <div className="flex-1 flex flex-col items-center justify-center text-center py-6">
            <p className="font-display text-[90px] md:text-[130px] leading-none font-black bg-gradient-to-br from-[#E31B23] via-brand-orange to-[#EAB308] bg-clip-text text-transparent">
              {stats.artists}
            </p>
            <p className="text-lg md:text-xl uppercase tracking-[0.3em] font-bold text-white/70 mt-2">
              artistes malgaches
            </p>
            <p className="text-sm text-white/50 mt-3 max-w-sm">
              Référencés sur la plateforme dans 6 disciplines
            </p>
          </div>

          {/* Grid 6 stats */}
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            {RUBRIQUES.map((r) => (
              <div
                key={r.key}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center backdrop-blur-sm"
              >
                <r.icon size={14} className="mx-auto text-brand-orange mb-1" />
                <p className={`font-display text-2xl md:text-3xl font-black bg-gradient-to-br ${r.color} bg-clip-text text-transparent`}>
                  {byCategory[r.key] ?? "—"}
                </p>
                <p className="text-[9px] md:text-[10px] uppercase tracking-widest text-white/60 mt-0.5">{r.label}</p>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
            <p className="text-xs text-white/50">🌱 <span className="text-white/90 font-semibold">voamada.com</span></p>
            <p className="text-[10px] tracking-[0.25em] uppercase text-brand-red font-bold">Revendique ton profil</p>
          </div>
        </div>
      </div>

      {/* Screenshot hint */}
      <div className="max-w-[720px] mx-auto mt-4 text-center text-xs text-white/40">
        💡 Screenshot au format 1080×1080 pour Instagram · 1200×630 pour Facebook · 1920×1080 pour YouTube banner
      </div>

      {/* CAPTIONS */}
      <div className="mt-16">
        <h2 className="font-display text-2xl md:text-3xl font-black mb-5 flex items-center gap-3">
          <Share2 size={22} className="text-brand-orange" />
          Légendes prêtes à copier
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {CAPTIONS.map((c, i) => {
            const interpolated = interpolate(c.text, {
              ...stats,
              music: byCategory.music ?? "—",
              dance: byCategory.dance ?? "—",
              humor: byCategory.humor ?? "—",
              art: byCategory.art ?? "—",
              cinema: byCategory.cinema ?? "—",
              fashion: byCategory.fashion ?? "—",
              food: byCategory.food ?? "—",
            });
            return (
              <div
                key={c.title}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex flex-col"
                data-testid={`caption-${i}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs uppercase tracking-widest text-white/50 font-bold">{c.title}</p>
                  <button
                    onClick={() => doCopy(i, c.text)}
                    className="text-brand-orange hover:text-white transition-colors"
                    data-testid={`copy-caption-${i}`}
                    aria-label="Copier"
                  >
                    {copied === i ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <pre className="text-xs text-white/75 whitespace-pre-wrap font-sans leading-relaxed flex-1">
                  {interpolated}
                </pre>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PromoStatsWall;
